import { ref, onBeforeUnmount } from 'vue'
import { useEventListener } from '@vueuse/core'
import { PANEL_CONFIG } from './usePanels'

type Side = 'left' | 'right'

interface ClickDetectionState {
  isTracking: boolean
  startTime: number
  startX: number
  startY: number
  hasMoved: boolean
}

interface DragClickCallbacks {
  onTabClick: (e: Event) => void
  onDragStart: (e: PointerEvent | MouseEvent | TouchEvent) => void
}

export function usePanelDragClick(callbacks: DragClickCallbacks) {
  // State for click detection
  const clickDetection = ref<ClickDetectionState>({
    isTracking: false,
    startTime: 0,
    startX: 0,
    startY: 0,
    hasMoved: false
  })

  // Track temporary listeners for cleanup
  const temporaryListeners = ref<{
    cleanup: (() => void) | null
  }>({
    cleanup: null
  })

  // Central event manager for temporary document listeners
  function createTemporaryListeners(handlers: {
    move: (e: PointerEvent | MouseEvent | TouchEvent) => void
    end: (e: PointerEvent | MouseEvent | TouchEvent) => void
  }) {
    const cleanupFunctions: Array<() => void> = []
    
    // Use VueUse for temporary listeners (automatically cleaned up on component unmount)
    const pointerMoveCleanup = useEventListener(document, 'pointermove', handlers.move, { passive: false })
    const pointerUpCleanup = useEventListener(document, 'pointerup', handlers.end, { passive: false })
    const mouseMoveCleanup = useEventListener(document, 'mousemove', handlers.move, { passive: false })
    const mouseUpCleanup = useEventListener(document, 'mouseup', handlers.end, { passive: false })
    const touchMoveCleanup = useEventListener(document, 'touchmove', handlers.move, { passive: false })
    const touchEndCleanup = useEventListener(document, 'touchend', handlers.end, { passive: false })
    
    cleanupFunctions.push(
      pointerMoveCleanup,
      pointerUpCleanup,
      mouseMoveCleanup,
      mouseUpCleanup,
      touchMoveCleanup,
      touchEndCleanup
    )
    
    // Return cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup())
      temporaryListeners.value.cleanup = null
    }
  }

  // Smart pointer handling that allows both clicking and dragging
  function handleTabPointerDown(e: PointerEvent | MouseEvent | TouchEvent, isDragging: boolean) {
    // Don't interfere if already dragging
    if (isDragging) return
    
    // Prevent this event from bubbling to the panel's pointerdown handler
    e.stopPropagation()
    
    // Clean up any existing temporary listeners first
    if (temporaryListeners.value.cleanup) {
      temporaryListeners.value.cleanup()
    }
    
    // Start tracking for click detection
    clickDetection.value = {
      isTracking: true,
      startTime: Date.now(),
      startX: 'clientX' in e ? e.clientX : e.touches?.[0]?.clientX || 0,
      startY: 'clientY' in e ? e.clientY : e.touches?.[0]?.clientY || 0,
      hasMoved: false
    }
    
    // Set up temporary listeners using central event manager
    const handleMove = (moveEvent: PointerEvent | MouseEvent | TouchEvent) => {
      if (!clickDetection.value.isTracking) return
      
      const currentX = 'clientX' in moveEvent ? moveEvent.clientX : moveEvent.touches?.[0]?.clientX || 0
      const currentY = 'clientY' in moveEvent ? moveEvent.clientY : moveEvent.touches?.[0]?.clientY || 0
      
      const deltaX = Math.abs(currentX - clickDetection.value.startX)
      const deltaY = Math.abs(currentY - clickDetection.value.startY)
      
      // Scale drag threshold with devicePixelRatio for better high-DPI mobile experience
      const dragThreshold = PANEL_CONFIG.DRAG_THRESHOLD_BASE * (window.devicePixelRatio || 1)
      
      // If moved more than scaled threshold, consider it a drag
      if (deltaX > dragThreshold || deltaY > dragThreshold) {
        clickDetection.value.hasMoved = true
        // Clean up and let the main drag system take over
        if (temporaryListeners.value.cleanup) {
          temporaryListeners.value.cleanup()
        }
        // Re-trigger the original pointerdown for dragging (without stopPropagation)
        callbacks.onDragStart(e)
      }
    }
    
    const handleEnd = () => {
      if (!clickDetection.value.isTracking) return
      
      const duration = Date.now() - clickDetection.value.startTime
      
      // If it was quick (< 300ms) and didn't move much, treat as click
      if (!clickDetection.value.hasMoved && duration < PANEL_CONFIG.CLICK_TIMEOUT) {
        // Execute click immediately, no delay needed
        callbacks.onTabClick(e)
      }
      
      if (temporaryListeners.value.cleanup) {
        temporaryListeners.value.cleanup()
      }
      
      // Reset tracking state
      clickDetection.value.isTracking = false
    }
    
    // Set up temporary listeners using central event manager
    temporaryListeners.value.cleanup = createTemporaryListeners({
      move: handleMove,
      end: handleEnd
    })
  }

  // Handle panel tab click to toggle panel (open/close)
  function createTabClickHandler(isOpen: boolean, side: Side, onToggle: (side: Side) => void, onClose: () => void) {
    return (e: Event) => {
      // Prevent event from bubbling to panel handlers
      e.stopPropagation()
      e.preventDefault()
      
      if (isOpen) {
        // Close the panel if it's currently open
        onClose()
      } else {
        // Open the panel if it's currently closed (including preview mode)
        onToggle(side)
      }
    }
  }

  // Handle panel preview click to open (anywhere on panel EXCEPT the tab)
  function createPanelPreviewClickHandler(
    isPreview: boolean,
    isOpen: boolean,
    isDragging: boolean,
    side: Side,
    onToggle: (side: Side) => void
  ) {
    return (e: Event) => {
      // Only work in preview mode when panel is not open
      if (!isPreview || isOpen || isDragging) return
      
      // Don't interfere if the click came from the tab (tab has its own handler)
      const target = e.target as HTMLElement
      if (target?.closest('.panel-tab')) return
      
      // Open the panel
      onToggle(side)
    }
  }

  // Extract coordinates from different event types
  function getEventCoordinates(e: PointerEvent | MouseEvent | TouchEvent): { x: number; y: number } {
    if ('clientX' in e) {
      return { x: e.clientX, y: e.clientY }
    }
    if ('touches' in e && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY }
    }
    return { x: 0, y: 0 }
  }

  // Calculate movement distance
  function calculateMovement(
    startCoords: { x: number; y: number },
    currentCoords: { x: number; y: number }
  ): number {
    const deltaX = Math.abs(currentCoords.x - startCoords.x)
    const deltaY = Math.abs(currentCoords.y - startCoords.y)
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY)
  }

  // Check if movement exceeds drag threshold
  function exceedsDragThreshold(movement: number): boolean {
    const dragThreshold = PANEL_CONFIG.DRAG_THRESHOLD_BASE * (window.devicePixelRatio || 1)
    return movement > dragThreshold
  }

  // Cleanup function
  function cleanup() {
    // Clean up any temporary document listeners
    if (temporaryListeners.value.cleanup) {
      temporaryListeners.value.cleanup()
    }
    
    // Reset click detection state
    clickDetection.value = {
      isTracking: false,
      startTime: 0,
      startX: 0,
      startY: 0,
      hasMoved: false
    }
  }

  // Cleanup on component unmount
  onBeforeUnmount(cleanup)

  return {
    // State
    clickDetection,
    
    // Core handlers
    handleTabPointerDown,
    
    // Factory functions
    createTabClickHandler,
    createPanelPreviewClickHandler,
    
    // Utility functions
    getEventCoordinates,
    calculateMovement,
    exceedsDragThreshold,
    
    // Cleanup
    cleanup
  }
} 